
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  title: 'Implementation Guide',
  section: 'Implementation Guide',
  layout: 'guide',
  description: 'Plaid Exchange Implementation guide'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Implementation Guide' subtitle={metadata.description} mdxType="Header" />
    <h2 {...{
      "id": "scoping-and-implementation-setup"
    }}>{`Scoping and implementation setup`}</h2>
    <p>{`Review the `}<a parentName="p" {...{
        "href": "/plaid-exchange/docs/reference"
      }}>{`Plaid Exchange API Reference documentation`}</a></p>
    <p>{`Prepare one or more test accounts for each type of account you support (checking, savings, credit card, loan, 401k, etc.) Ensure that:`}</p>
    <ul>
      <li parentName="ul">{`All accounts have balance data.`}</li>
      <li parentName="ul">{`All accounts have contact data.`}</li>
      <li parentName="ul">{`Depository, loan, and investment accounts have transaction data.`}</li>
      <li parentName="ul">{`Depository accounts have payment networks data.`}</li>
      <li parentName="ul">{`Negative test accounts (accounts in a bad state).`}</li>
    </ul>
    <h3 {...{
      "id": "ip-allowlist"
    }}>{`IP allowlist`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`["18.214.218.91", "3.211.30.208", "3.214.25.67", "54.88.74.128", "54.208.59.10", "54.88.202.28", "34.199.37.46", "35.168.137.48", "3.215.49.214", "34.202.178.138", "52.0.205.192", "52.3.166.211", "35.174.147.86", "52.88.82.239", "52.41.247.19", "3.233.249.56", "35.153.85.253", "3.219.116.195"]
`}</code></pre>
    <p>{`If in testing, also add the following`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`["104.131.160.201", "3.80.88.60"]
`}</code></pre>
    <h3 {...{
      "id": "building-and-testing"
    }}>{`Building and testing`}</h3>
    <h5 {...{
      "id": "develop-and-test-authentication"
    }}>{`Develop and test authentication`}</h5>
    <ul>
      <li parentName="ul">{`Issue Plaid a `}<a parentName="li" {...{
          "href": "/plaid-exchange/docs/authentication/#client-id-and-client-secret"
        }}>{`Client ID and Secret`}</a>{`.`}</li>
      <li parentName="ul">{`Build / Test OAuth flow according to `}<a parentName="li" {...{
          "href": "/plaid-exchange/docs/authentication"
        }}>{`guidelines`}</a>{`.`}</li>
    </ul>
    <p>{`Note: Plaid Exchange can support MFA/OTP authorization patterns, but it is not recommended.`}</p>
    <h5 {...{
      "id": "develop-and-test-plaid-exchange-endpoints"
    }}>{`Develop and test Plaid Exchange Endpoints`}</h5>
    <p>{`Use the `}<a parentName="p" {...{
        "href": "/plaid-exchange/docs/reference"
      }}>{`Plaid Exchange API Reference`}</a>{` to guide endpoint creation.`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`/oauth2/auth`}</inlineCode>{`: OAuth 2.0 Auth flow`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`/oauth2/token`}</inlineCode>{`: OAuth 2.0 Token flow`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`/users/{user_id}`}</inlineCode>{`: Retrieve account and identity information`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`/users/{user_id}/transactions`}</inlineCode>{`: Retrieve transaction history`}</li>
    </ul>
    <h3 {...{
      "id": "go-live"
    }}>{`Go-live`}</h3>
    <h5 {...{
      "id": "get-ready-for-production"
    }}>{`Get ready for production`}</h5>
    <p>{`Confirm your organization's name, logo, and URL. Plaid uses this information to populate your organization's details in Plaid Link. Your logo file must be a 96x96 circular PNG and under 2MB
If you would like to make an external launch announcement, reach out to our Plaid team to finalize plans`}</p>
    <h5 {...{
      "id": "pilot-and-migrate"
    }}>{`Pilot and migrate`}</h5>
    <p>{`(Note: This section only applies to existing Plaid partners)`}</p>
    <p>{`If your institution is already supported in Plaid Link or you are a platform provider with numerous institutions, we will partner together to develop a pilot and migration plan. Migrations typically have 4 phases:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}></th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Phase`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Timeline`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`1`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Friends and Family Testing`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Limited access, internal user traffic`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`~ 2 weeks`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`2`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Pilot Cohort`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Enable new connections in production for pilot cohort, monitor health and resolve issues`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`~ 2 weeks`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`3`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Remaining Cohorts`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Enable new connections in production for pilot cohort, monitor health and resolve issues # of cohorts is partner dependent`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`~ 2-3 weeks`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`4`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Existing User Migration`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`After integration health is validated, Plaid will begin a process to migrate all existing items over to the OAuth + API connection.`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`~120 days`}</td>
        </tr>
      </tbody>
    </table>
    <ul>
      <li parentName="ul">{`Timeline illustrative. Speed contingent on ability to validate institutional health, address any open issues etc.`}</li>
    </ul>
    <h3 {...{
      "id": "ongoing-management"
    }}>{`Ongoing management`}</h3>
    <p>{`We will continue to partner together to ensure integration quality after you go live on API connectivity with Plaid. Key integration health metrics Plaid monitors include: conversion, data access success rate, data availability and accuracy.`}</p>
    <p>{`Update the Plaid team with the correct point of contact to support ongoing integration health
Log the variables below to help troubleshoot any potential issues with the integration:`}</p>
    <ul>
      <li parentName="ul">{`OAuth flow: log `}<inlineCode parentName="li">{`oauth_state_id`}</inlineCode></li>
      <li parentName="ul">{`API connections: log `}<inlineCode parentName="li">{`FI_UNIQUE_ID`}</inlineCode></li>
    </ul>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "scoping-and-implementation-setup",
        level: 2,
        title: "Scoping and implementation setup",
      },
{
        id: "ip-allowlist",
        level: 3,
        title: "IP allowlist",
      },
{
        id: "building-and-testing",
        level: 3,
        title: "Building and testing",
      },
{
        id: "go-live",
        level: 3,
        title: "Go-live",
      },
{
        id: "ongoing-management",
        level: 3,
        title: "Ongoing management",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
